<template>
  <v-container class="credits-page px-6 px-md-10 py-8">
    <div class="credits-header mb-10">
      <div>
        <h1>Créditos</h1>
        <p>Administra y monitorea tu consumo de créditos</p>
      </div>

      <v-btn
        depressed
        class="buy-credits-button"
        @click="handleBuyCredits"
      >
        Comprar créditos
      </v-btn>
    </div>

    <v-alert
      v-if="error"
      type="error"
      outlined
      class="mb-6"
    >
      {{ error }}
    </v-alert>

    <div v-if="loading">
      <v-skeleton-loader type="card" class="mb-8" />
      <v-skeleton-loader type="card, card, list-item-three-line" />
    </div>

    <div v-else>
      <section class="mb-10">
        <v-card class="credits-summary-card pa-6 pa-md-8" elevation="2">
          <div class="summary-main">
            <div>
              <p class="summary-label-main mb-3">Créditos totales disponibles</p>
              <h2>{{ formatNumber(creditsSummary.totalAvailable) }}</h2>
              <p class="summary-detail mb-0">
                {{ formatNumber(creditsSummary.monthlyAvailable) }} mensuales +
                {{ formatNumber(creditsSummary.purchasedAvailable) }} comprados
              </p>
            </div>

            <div class="summary-large-icon">
              <v-icon color="white" size="42">mdi-creation-outline</v-icon>
            </div>
          </div>

          <v-row class="mt-8">
            <v-col
              v-for="stat in summaryStats"
              :key="stat.label"
              cols="12"
              md="4"
            >
              <div class="summary-stat-card">
                <div class="d-flex align-center mb-3">
                  <v-icon color="white" class="mr-3">{{ stat.icon }}</v-icon>
                  <span>{{ stat.label }}</span>
                </div>
                <strong>{{ stat.value }}</strong>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </section>

      <section class="mb-10">
        <v-row>
          <v-col
            v-for="bucket in creditBuckets"
            :key="bucket.title"
            cols="12"
            md="6"
          >
            <v-card class="bucket-card pa-6" elevation="2">
              <div class="bucket-header">
                <div>
                  <h2>{{ bucket.title }}</h2>
                  <p>{{ formatNumber(bucket.used) }} de {{ formatNumber(bucket.total) }}</p>
                </div>

                <div class="bucket-icon" :class="bucket.iconClass">
                  <v-icon color="white">{{ bucket.icon }}</v-icon>
                </div>
              </div>

              <v-progress-linear
                :value="getUsagePercent(bucket.used, bucket.total)"
                height="11"
                rounded
                background-color="#D0D0D5"
                color="#050417"
                class="bucket-progress"
              />

              <p class="bucket-note mb-0">{{ bucket.note }}</p>
            </v-card>
          </v-col>
        </v-row>
      </section>

      <section class="mb-12">
        <h2 class="section-title mb-7">Consumo por acción (este mes)</h2>

        <v-card v-if="usageByAction.length" class="usage-card pa-5" elevation="2">
          <div
            v-for="action in usageByAction"
            :key="action.id"
            class="usage-row"
          >
            <div class="usage-left">
              <div class="usage-icon">
                <v-icon :color="action.color">{{ action.icon }}</v-icon>
              </div>

              <div>
                <h3>{{ action.title }}</h3>
                <p>{{ action.description }}</p>
              </div>
            </div>

            <div class="usage-right">
              <strong>{{ formatNumber(action.credits) }} créditos</strong>
              <p>{{ action.percent }}% del total</p>
            </div>
          </div>
        </v-card>

        <v-card v-else class="empty-card pa-6" outlined>
          Todavía no hay consumo registrado este mes.
        </v-card>
      </section>

      <section>
        <h2 class="section-title mb-7">Historial de créditos</h2>

        <v-card v-if="creditHistory.length" class="history-card pa-5" elevation="2">
          <div
            v-for="(entry, index) in creditHistory"
            :key="entry.id"
            class="history-row"
            :class="{ 'with-border': index < creditHistory.length - 1 }"
          >
            <div>
              <h3>{{ entry.title }}</h3>
              <p>{{ entry.date }}</p>
            </div>

            <div class="history-right">
              <strong :class="{ positive: entry.amount > 0 }">
                {{ formatSignedAmount(entry.amount) }}
              </strong>
              <p>Balance: {{ entry.balance !== null ? formatNumber(entry.balance) : 'N/A' }}</p>
            </div>
          </div>
        </v-card>

        <v-card v-else class="empty-card pa-6" outlined>
          Todavía no hay movimientos de créditos.
        </v-card>
      </section>
    </div>
  </v-container>
</template>

<script>
export default {
  name: 'Credits',
  data() {
    return {
      loading: true,
      error: null,
      creditsSummary: {
        totalAvailable: 0,
        monthlyAvailable: 0,
        purchasedAvailable: 0,
        monthlyUsed: 0,
        renewalDate: 'N/A',
        dailyAverage: 0,
      },
      creditBuckets: [],
      usageByAction: [],
      creditHistory: [],
    };
  },
  computed: {
    summaryStats() {
      return [
        {
          label: 'Consumo (mes)',
          value: this.formatNumber(this.creditsSummary.monthlyUsed),
          icon: 'mdi-trending-down',
        },
        {
          label: 'Renovación',
          value: this.creditsSummary.renewalDate,
          icon: 'mdi-calendar-month-outline',
        },
        {
          label: 'Promedio diario',
          value: this.formatNumber(this.creditsSummary.dailyAverage),
          icon: 'mdi-flash-outline',
        },
      ];
    },
  },
  methods: {
    getLanguage() {
      return this.$store?.state?.language || localStorage.getItem('lang') || 'en';
    },
    getAuthConfig() {
      return {
        headers: {
          Authorization: this.$store.state.sToken
            ? `Bearer ${this.$store.state.sToken}`
            : '',
        },
      };
    },
    apiUrl(path) {
      return `${URI}/api/v1/${this.getLanguage()}/billing${path}`;
    },
    formatNumber(value) {
      return Number(value).toLocaleString('en-US');
    },
    formatSignedAmount(value) {
      return value > 0 ? `+${this.formatNumber(value)}` : `${this.formatNumber(value)}`;
    },
    formatDate(value) {
      if (!value) return 'N/A';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    formatDateTime(value) {
      if (!value) return 'N/A';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return 'N/A';
      return date.toLocaleString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    getUsagePercent(used, total) {
      if (!total) return 0;
      return Math.min((used / total) * 100, 100);
    },
    actionIcon(creditType) {
      const normalized = String(creditType || '').toLowerCase();
      if (normalized.includes('call')) return { icon: 'mdi-phone-outline', color: '#2163FF' };
      if (normalized.includes('message') || normalized.includes('whatsapp')) return { icon: 'mdi-message-outline', color: '#00A342' };
      if (normalized.includes('analysis') || normalized.includes('price')) return { icon: 'mdi-chart-bar', color: '#FF5B00' };
      if (normalized.includes('campaign')) return { icon: 'mdi-flash-outline', color: '#2163FF' };
      return { icon: 'mdi-creation-outline', color: '#2163FF' };
    },
    buildUsageByAction(movements, monthlyUsed) {
      const consumes = (movements || []).filter((mv) => String(mv.movementType || '').toLowerCase() === 'consume');
      const grouped = consumes.reduce((acc, mv) => {
        const key = mv.creditType || 'general';
        acc[key] = (acc[key] || 0) + Math.abs(Number(mv.amount || 0));
        return acc;
      }, {});
      return Object.entries(grouped)
        .map(([creditType, credits]) => {
          const iconStyle = this.actionIcon(creditType);
          return {
            id: creditType,
            title: `Consumo ${creditType}`,
            description: 'Créditos consumidos',
            credits,
            percent: monthlyUsed > 0 ? Number(((credits / monthlyUsed) * 100).toFixed(1)) : 0,
            icon: iconStyle.icon,
            color: iconStyle.color,
          };
        })
        .sort((a, b) => b.credits - a.credits);
    },
    buildCreditHistory(movements) {
      let runningBalance = 0;
      const sorted = [...(movements || [])].sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
      const withBalance = sorted.map((mv) => {
        runningBalance += Number(mv.amount || 0);
        return {
          id: mv.id,
          title: mv.movementType === 'consume' ? `Consumo (${mv.creditType || 'general'})` : `Abono (${mv.creditType || 'general'})`,
          date: this.formatDateTime(mv.createdAt),
          amount: Number(mv.amount || 0),
          balance: runningBalance,
        };
      });
      return withBalance.reverse();
    },
    async loadCredits() {
      this.loading = true;
      this.error = null;
      try {
        const config = this.getAuthConfig();
        const [creditsRes, statusRes, movementsRes] = await Promise.all([
          DB.get(this.apiUrl('/credits'), config),
          DB.get(this.apiUrl('/status'), config),
          DB.get(this.apiUrl('/credit-movements?limit=100'), config),
        ]);

        const summaryRows = creditsRes?.data?.credits || [];
        const monthlyAvailable = summaryRows
          .filter((row) => String(row.creditType || '').toLowerCase().includes('monthly'))
          .reduce((sum, row) => sum + Number(row.available || 0), 0);
        const purchasedAvailable = summaryRows
          .filter((row) => !String(row.creditType || '').toLowerCase().includes('monthly'))
          .reduce((sum, row) => sum + Number(row.available || 0), 0);
        const totalAvailable = summaryRows.reduce((sum, row) => sum + Number(row.available || 0), 0);
        const movements = movementsRes?.data?.movements || [];
        const monthlyUsed = movements
          .filter((mv) => String(mv.movementType || '').toLowerCase() === 'consume')
          .reduce((sum, mv) => sum + Math.abs(Number(mv.amount || 0)), 0);

        const sub = statusRes?.data?.subscription || null;
        let dailyAverage = 0;
        if (sub?.currentPeriodStart) {
          const start = new Date(sub.currentPeriodStart);
          const days = Math.max(1, Math.ceil((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24)));
          dailyAverage = Math.round(monthlyUsed / days);
        }

        this.creditsSummary = {
          totalAvailable,
          monthlyAvailable,
          purchasedAvailable,
          monthlyUsed,
          renewalDate: this.formatDate(sub?.currentPeriodEnd),
          dailyAverage,
        };

        const monthlyTotal = monthlyAvailable + monthlyUsed;
        const purchasedTotal = purchasedAvailable;
        this.creditBuckets = [
          {
            title: 'Créditos Mensuales',
            used: monthlyUsed,
            total: monthlyTotal,
            note: 'Se renuevan cada ciclo de facturación',
            icon: 'mdi-creation-outline',
            iconClass: 'bucket-icon-blue',
          },
          {
            title: 'Créditos Comprados',
            used: 0,
            total: purchasedTotal,
            note: 'No expiran, se acumulan',
            icon: 'mdi-creation-outline',
            iconClass: 'bucket-icon-purple',
          },
        ];

        this.usageByAction = this.buildUsageByAction(movements, monthlyUsed);
        this.creditHistory = this.buildCreditHistory(movements);
      } catch (err) {
        this.error = err?.response?.data?.message || err.message || 'No se pudo cargar créditos';
      } finally {
        this.loading = false;
      }
    },
    handleBuyCredits() {
      this.$router.push('/bills').catch(() => {});
    },
  },
  mounted() {
    this.loadCredits();
  },
};
</script>

<style scoped>
.credits-page {
  min-height: 100vh;
  background: #f7f8fa;
  color: #0c0d10;
}

.credits-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.credits-header h1 {
  font-family: 'montserrat-bold';
  font-size: 40px;
  line-height: 1.15;
  margin-bottom: 14px;
  color: #0c0d10;
}

.credits-header p {
  font-family: 'montserrat-semibold';
  font-size: 22px;
  color: #465266;
  margin-bottom: 0;
}

.buy-credits-button {
  min-width: 196px;
  height: 48px !important;
  border-radius: 8px;
  background: #1876d2;
  color: white !important;
  font-family: 'montserrat-bold';
  font-size: 16px;
  text-transform: none;
}

.credits-summary-card,
.bucket-card,
.usage-card,
.history-card,
.empty-card {
  border-radius: 16px;
}

.credits-summary-card {
  background: #f5f9fd;
  color: white;
}

.summary-main {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.summary-label-main {
  font-family: 'montserrat-semibold';
  font-size: 21px;
}

.summary-main h2 {
  font-family: 'montserrat-bold';
  font-size: 64px;
  line-height: 1;
  margin-bottom: 12px;
}

.summary-detail {
  font-family: 'montserrat-bold';
  font-size: 18px;
}

.summary-large-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 86px;
  height: 86px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.18);
}

.summary-stat-card {
  min-height: 122px;
  padding: 22px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.14);
  font-family: 'montserrat-bold';
}

.summary-stat-card span {
  font-size: 18px;
}

.summary-stat-card strong {
  display: block;
  font-size: 32px;
  line-height: 1;
}

.bucket-card {
  min-height: 260px;
  background: white;
}

.bucket-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 52px;
}

.bucket-header h2 {
  font-family: 'montserrat-bold';
  font-size: 25px;
  margin-bottom: 8px;
}

.bucket-header p,
.bucket-note {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
}

.bucket-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  height: 54px;
  border-radius: 12px;
}

.bucket-icon-blue {
  background: #0c86d5;
}

.bucket-icon-purple {
  background: #0c86d5;
}

.bucket-progress {
  margin-bottom: 40px;
}

.section-title {
  font-family: 'montserrat-bold';
  font-size: 28px;
  line-height: 1.2;
  color: #0c0d10;
}

.usage-card,
.history-card {
  background: white;
}

.usage-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 16px 12px;
}

.usage-left {
  display: flex;
  align-items: center;
  gap: 22px;
}

.usage-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  height: 54px;
  border-radius: 12px;
  background: #f0f1f5;
}

.usage-left h3,
.history-row h3 {
  font-family: 'montserrat-bold';
  font-size: 21px;
  margin-bottom: 6px;
}

.usage-left p,
.usage-right p,
.history-row p {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
  margin: 0;
}

.usage-right {
  min-width: 170px;
  text-align: right;
}

.usage-right strong {
  display: block;
  font-family: 'montserrat-bold';
  font-size: 21px;
  margin-bottom: 6px;
}

.history-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  padding: 28px 12px;
}

.history-row.with-border {
  border-bottom: 1px solid #edf0f3;
}

.history-right {
  min-width: 180px;
  text-align: right;
}

.history-right strong {
  display: block;
  font-family: 'montserrat-bold';
  font-size: 22px;
  color: #111827;
  margin-bottom: 8px;
}

.history-right strong.positive {
  color: #00a342;
}

.empty-card {
  font-family: 'montserrat-semibold';
  color: #626d80;
  background: white;
}

@media (max-width: 960px) {
  .credits-page {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .credits-header,
  .summary-main,
  .usage-row,
  .history-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .credits-header h1 {
    font-size: 32px;
  }

  .credits-header p {
    font-size: 18px;
  }

  .buy-credits-button {
    width: 100%;
  }

  .summary-main h2 {
    font-size: 52px;
  }

  .summary-large-icon {
    display: none;
  }

  .bucket-header {
    margin-bottom: 36px;
  }

  .usage-right,
  .history-right {
    width: 100%;
    min-width: 0;
    padding-left: 76px;
    text-align: left;
  }

  .history-right {
    padding-left: 0;
  }
}

/* Fresher AI shared visual language */
.credits-page {
  width: 100%;
  max-width: 1740px !important;
  margin: 0 auto;
  padding: 28px 40px 32px !important;
  font-family: 'montserrat-regular';
}

.credits-header h1 {
  margin-bottom: 5px;
  font-size: 28px;
  line-height: 1.2;
}

.credits-header p {
  font-family: 'montserrat-regular';
  font-size: 14px;
  color: #718096;
}

.buy-credits-button {
  min-width: 174px;
  height: 42px !important;
  background: #1876d2 !important;
  font-size: 13px;
}

.credits-summary-card,
.bucket-card,
.usage-card,
.history-card,
.empty-card {
  border: 1px solid #e2e7ed !important;
  border-radius: 12px !important;
  box-shadow: 0 3px 12px rgba(30, 47, 70, 0.04) !important;
}

.credits-summary-card {
  background: #f5f9fd;
  color: #0c0d10;
}

.summary-label-main {
  font-family: 'montserrat-regular';
  font-size: 14px;
  color: #596579;
}

.summary-main h2 {
  font-size: 42px;
}

.summary-detail {
  font-family: 'montserrat-regular';
  font-size: 13px;
  color: #718096;
}

.summary-large-icon {
  width: 58px;
  height: 58px;
  border-radius: 14px;
  background: #eaf4fd;
}

.summary-stat-card {
  min-height: auto;
  padding: 18px;
  border: 1px solid #e2e7ed;
  border-radius: 10px;
  background: #ffffff;
  color: #0c0d10;
}

.summary-stat-card span {
  font-family: 'montserrat-regular';
  font-size: 12px;
  color: #718096;
}

.summary-stat-card strong {
  margin-top: 6px;
  font-size: 24px;
}

.bucket-card {
  min-height: auto;
}

.bucket-header {
  margin-bottom: 28px;
}

.bucket-header h2 {
  font-size: 19px;
}

.bucket-header p,
.bucket-note {
  font-family: 'montserrat-regular';
  font-size: 13px;
  color: #718096;
}

.bucket-progress {
  margin-bottom: 24px;
}

@media (max-width: 960px) {
  .credits-page {
    padding: 24px !important;
  }

  .credits-header h1 {
    font-size: 26px;
  }

  .credits-header p {
    font-size: 13px;
  }
}

@media (max-width: 600px) {
  .credits-page {
    padding: 20px 16px 28px !important;
  }

  .summary-main h2 {
    font-size: 36px;
  }
}
</style>
